import type { ReactNode } from "react";
import type {
  AgentDefinition,
  IntegrationSnapshot,
  Observation,
  UnifiedAuditEvent,
} from "@barmetvault/shared";
import { integrationStage } from "@barmetvault/shared";
export function StatusBadge({ status }: { status: string }) {
  return (
    <span className="hub-badge" data-state={status}>
      {status}
    </span>
  );
}
export function PageHeader({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children?: ReactNode;
}) {
  return (
    <header className="hub-page-header">
      <div>
        <p className="hub-eyebrow">BarmetHub / Operaciones</p>
        <h1>{title}</h1>
        {subtitle && <p className="hub-muted">{subtitle}</p>}
      </div>
      <div>{children}</div>
    </header>
  );
}
export function EmptyState({
  title = "Sin datos disponibles",
  detail = "Todavía no hay registros verificados para esta vista.",
}: {
  title?: string;
  detail?: string;
}) {
  return (
    <div className="hub-empty">
      <StatusBadge status="NO_DATA" />
      <h3>{title}</h3>
      <p>{detail}</p>
    </div>
  );
}
export function ErrorState({ retry }: { retry?: () => void }) {
  return (
    <div role="alert" className="hub-alert">
      <StatusBadge status="ERROR" />
      <p>
        No se pudo verificar la fuente. No se muestran datos anteriores como
        actuales.
      </p>
      {retry && (
        <button className="hub-button" onClick={retry}>
          Reintentar
        </button>
      )}
    </div>
  );
}
export function LoadingState() {
  return (
    <div role="status" className="hub-empty" aria-busy="true">
      Consultando fuentes…
    </div>
  );
}
export function PermissionDenied() {
  return (
    <div role="alert" className="hub-empty">
      <StatusBadge status="PERMISSION_DENIED" />
      <h2>Acceso restringido</h2>
      <p>Tu cuenta no tiene permisos para esta operación.</p>
    </div>
  );
}
export function AlertPanel({ children }: { children: ReactNode }) {
  return (
    <section className="hub-card hub-alert">
      <h2>Atención requerida</h2>
      {children}
    </section>
  );
}
export function MetricCard({
  title,
  metric,
}: {
  title: string;
  metric: Observation<number>;
}) {
  return (
    <article className="hub-card">
      <h3>{title}</h3>
      <strong className="hub-metric">
        {metric.state === "REAL_DATA"
          ? new Intl.NumberFormat("es-ES").format(metric.data)
          : "—"}
      </strong>
      <StatusBadge status={metric.state} />
      <p className="hub-muted">Fuente: {metric.source}</p>
      {metric.observedAt && (
        <time dateTime={metric.observedAt}>
          {new Date(metric.observedAt).toLocaleString("es-ES")}
        </time>
      )}
    </article>
  );
}
export function DataTable<T>({
  rows,
  columns,
  rowKey,
}: {
  rows: readonly T[];
  columns: readonly { title: string; render: (row: T) => ReactNode }[];
  rowKey: (row: T) => string;
}) {
  if (!rows.length) return <EmptyState />;
  return (
    <div className="hub-table-scroll">
      <table className="hub-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.title} scope="col">
                {c.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)}>
              {columns.map((c) => (
                <td key={c.title}>{c.render(row)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
export function IntegrationCard({ item }: { item: IntegrationSnapshot }) {
  return (
    <article className="hub-card">
      <div className="hub-row">
        <h2>{item.name}</h2>
        <StatusBadge status={integrationStage(item)} />
      </div>
      <p className="hub-muted">{item.category}</p>
      <dl className="hub-dl">
        <dt>Configuración</dt>
        <dd>{item.configured ? "CONFIGURED" : "Sin configurar"}</dd>
        <dt>Conexión</dt>
        <dd>
          <StatusBadge status={item.connectionStatus} />
        </dd>
        <dt>Salud</dt>
        <dd>
          <StatusBadge status={item.healthStatus} />
        </dd>
        <dt>Última comprobación</dt>
        <dd>
          {item.lastCheck
            ? new Date(item.lastCheck).toLocaleString("es-ES")
            : "—"}
        </dd>
      </dl>
    </article>
  );
}
export function AgentCard({ agent }: { agent: AgentDefinition }) {
  return (
    <article className="hub-card">
      <h3>{agent.name}</h3>
      <p>{agent.role}</p>
      <StatusBadge status={agent.status} />
      <dl className="hub-dl">
        <dt>Skills</dt>
        <dd>{agent.skills.join(", ") || "—"}</dd>
        <dt>Tools</dt>
        <dd>{agent.tools.join(", ") || "—"}</dd>
        <dt>Integraciones</dt>
        <dd>{agent.integrations.join(", ") || "—"}</dd>
        <dt>Última ejecución</dt>
        <dd>{agent.lastExecution || "—"}</dd>
      </dl>
    </article>
  );
}
export function AuditFeed({
  events,
}: {
  events: readonly UnifiedAuditEvent[];
}) {
  return (
    <DataTable
      rows={events}
      rowKey={(e) => e.id}
      columns={[
        {
          title: "Actor",
          render: (e) => (
            <>
              {e.actor.type} · {e.actor.id}
            </>
          ),
        },
        { title: "Acción", render: (e) => e.action },
        { title: "Resultado", render: (e) => e.result },
        { title: "Fecha", render: (e) => <time>{e.timestamp}</time> },
      ]}
    />
  );
}
